// THE APPLICATION LANGUAGE — the language of the UI itself (labels, verbs, toasts, narrations),
// distinct from the correspondence language of an engagement (src/shared/language.ts), which
// governs the CONTENT the register holds.
//
// The catalogue is keyed by the ENGLISH text itself: `t('Save')` renders "Save" in English and
// looks up "Speichern" in German. English therefore needs no catalogue; a missing German entry
// falls back to English rather than to a key, so nothing ever renders as `btn.save`. Each page
// carries its own German catalogue beside it (`<Page>.de.ts`) and registers it on load, so a
// hundred files can be translated in parallel without one shared file to fight over.

export const UI_LANGS = ['en', 'de', 'fr'] as const;
export type UiLang = (typeof UI_LANGS)[number];
export const UI_LANG_LABEL: Record<UiLang, string> = { en: 'English', de: 'Deutsch', fr: 'Français' };
export const isUiLang = (v: unknown): v is UiLang => typeof v === 'string' && (UI_LANGS as readonly string[]).includes(v);

export type Dict = Record<string, string>;
export type Vars = Record<string, string | number | null | undefined>;

/** `{name}` placeholders; a missing var renders empty. */
export function interpolate(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (_, k: string) => (vars[k] === null || vars[k] === undefined ? '' : String(vars[k])));
}

/** A translator over one dictionary. `t(key, vars)` — key IS the English text. */
export function makeT(dict: Dict | null): (key: string, vars?: Vars) => string {
  return (key, vars) => interpolate(dict?.[key] ?? key, vars);
}

/** Plural by count: `tp(n, '{n} switch saved', '{n} switches saved')` — both forms are catalogue keys. */
export function makeTp(t: (key: string, vars?: Vars) => string) {
  return (n: number, one: string, other: string, vars?: Vars): string => t(n === 1 ? one : other, { n, ...(vars ?? {}) });
}

/**
 * Context variant: `tCtx(t, 'Hold', 'corpus')` looks up the composite key `Hold‖corpus`;
 * when the catalogue has no entry (always true in English), it falls back to t('Hold').
 * For the rare English word that means two different German words.
 */
export function tCtx(
  t: (key: string, vars?: Vars) => string,
  key: string,
  ctx: string,
  vars?: Vars,
): string {
  const composite = `${key}\u2016${ctx}`;
  const out = t(composite, vars);
  return out === interpolate(composite, vars) ? t(key, vars) : out;
}

/** Merge a catalogue into a target, noting (in dev) where two files disagree on one English key. */
export function mergeDict(into: Dict, add: Dict, source?: string): void {
  for (const [k, v] of Object.entries(add)) {
    if (into[k] !== undefined && into[k] !== v && typeof console !== 'undefined') {
      console.warn(`[i18n] "${k}" translated twice (${source ?? 'a catalogue'}): keeping "${into[k]}", ignoring "${v}"`);
      continue;
    }
    into[k] = v;
  }
}

/**
 * Locale for Intl. Every variant is the SWISS one, English included: this record is Swiss, and
 * the house rule (src/shared/format.ts, BUILD-BRIEF §2) writes dates dd.mm.yyyy in every
 * language. `en-GB` would render 23/08/2026 in the default UI and silently contradict the rule.
 */
export const intlLocale = (lang: UiLang): string => (lang === 'de' ? 'de-CH' : lang === 'fr' ? 'fr-CH' : 'en-CH');
